/******************************************
*  Author : Riccardo Di Dato
*  Created On : Fri Apr 29 2022
*  File : index.ts
*******************************************/
// import Cookies = require("js-cookie");
import Cookies from 'js-cookie';
import { isFunction, isString } from '../TypeChecking/index.js';
import { hasOwnProperty } from '../Utils/index.js';
import { param, unparam } from '../HTTP/utils.js';
/**
 * Imposta il valore di un cookie
 *
 * @author Riccardo Di Dato
 *
 * @param name Nome del cookie
 * @param content Contenuto del cookie o sua funzione generatrice
 * @param attributes Attributi del cookie
 */
export function set(name, content, attributes) {
    const useContent = isFunction(content) ? content() : content;
    Cookies.set(name, param(useContent), attributes);
}
/**
 * Restituisce:
 *  - il valore di un cookie se riceve `name`
 *  - una mappa di tutti i cookies esistenti se non riceve `name`
 *
 * @author Riccardo Di Dato
 *
 * @param name Se passato è il nome del cookie
 * @returns Valore del cookie o mappa di tutti
 */
export function get(name) {
    if (name === undefined) {
        const ck = Cookies.get();
        return Object.keys(ck).reduce((carry, cur) => {
            carry[cur] = unparam(ck[cur]);
            return carry;
        }, {});
    }
    else {
        const rval = Cookies.get(name);
        return isString(rval) ? unparam(rval) : rval;
    }
}
/**
 * Cancella un cookie a partire dal suo nome ed eventuali attributi
 *
 * @author Riccardo Di Dato
 *
 * @param name Nome del cookie da cancellare
 * @param attributes Attributi del cookie da cancellare
 */
export function remove(name, attributes) {
    Cookies.remove(name, attributes);
}
/**
 * Verifica l'esistenza di un cookie
 *
 * @author Riccardo Di Dato
 *
 * @param name nome del cookie
 * @returns True se esiste
 */
export function exists(name) {
    return hasOwnProperty(get(), name);
}
/**
 * Alias di {@link exists}
 *
 * @author Riccardo Di Dato
 *
 * @param name nome del cookie
 * @returns True se esiste
 */
export function cookieExists(name) {
    return exists(name);
}
/**
 * Helper per la scrittura di un cookie
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param name nome del cookie
 * @param expirationTs Timestamp in millisecondi della scadenza del cookie
 * @param content contenuto del cookie o funzione per generarlo
 * @param domain dominio del cookie
 * @param path path del cookie
 * @param secure cookie HTTPS
 * @param samesite specifica samesite (default: "Lax")
 */
export function writeCookie(name, expirationTs, content, domain, path, secure, samesite = "Lax") {
    set(name, content, {
        expires: new Date(expirationTs),
        domain: domain,
        path: path,
        sameSite: samesite,
        secure: secure
    });
}
/***
 * Scrive un cookie come {@link writeCookie} ma prende la durata in giorni invece che millisecondi
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param name nome del cookie
 * @param durationInDays durata in giorni del cookie
 * @param content contenuto del cookie
 * @param domain dominio del cookie
 * @param path path del cookie
 * @param secure cookie HTTPS
 * @param samesite specifica samesite (default: "Lax")
 */
export function setCookie(name, durationInDays, content, domain, path, secure, samesite = "Lax") {
    set(name, content, {
        expires: durationInDays,
        domain: domain,
        path: path,
        sameSite: samesite,
        secure: secure
    });
}
/***
 * Cancella un cookie
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param name nome del cookie da cancellare
 * @param domain dominio del cookie
 * @param path path del cookie
 */
export function deleteCookie(name, domain, path) {
    remove(name, {
        path: path,
        domain: domain
    });
}
/***
 * Alias di {@link deleteCookie}
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param name nome del cookie da cancellare
 * @param domain dominio del cookie
 * @param path path del cookie
 */
export function removeCookie(name, domain, path) {
    deleteCookie(name, domain, path);
}
/**
 * Alias di {@link get} chiamato senza passare un `name`
 *
 * @return ritorna un oggetto che ha come coppie chiave valore i cookie
 */
export function getCookies() {
    return get();
}
/**
 * Alias di {@link get} chiamato passando un `name`
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param cname nome del cookie da recuperare
 * @return Valore del cookie o undefined se non esiste
 */
export function getCookie(name) {
    return get(name);
}
/**
 *
 * Routing function per tutti gli helper in materia di cookie:
 *  - senza parametri: {@link getCookies}
 *  - inv. con 1 parametro `string`: {@link getCookie}
 *  - inv. con 1 parametro `CookieRoutingSetHelper`: {@link setCookie}
 *  - inv. con 2 o 3: {@link setCookie} (in caso di due parametri expires = 1)
 *
 * @deprecated
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param name
 * @param content
 * @param attributes
 * @returns
 */
export function cookie(name, content, attributes) {
    if (name === undefined) {
        return getCookies();
    }
    else if (!isString(name)) {
        setCookie(name.name, name.expires, name.content, name.domain, name.path, name.secure, name.samesite);
    }
    else {
        if (content === undefined) {
            return getCookie(name);
        }
        else {
            if (attributes === undefined) {
                attributes = {
                    expires: 1
                };
            }
            set(name, content, attributes);
        }
    }
}
